import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import StepPad from "#components/step-pad.gts";
import type { Channel } from "#models/channel.ts";
import type { Step } from "#models/step.ts";
import type { Sound } from "#models/sound.ts";
import PlusIcon from "~icons/mdi/plus-circle";
import MinusIcon from "~icons/mdi/minus-circle";
import DownIcon from "~icons/mdi/arrow-down-circle";
import UpIcon from "~icons/mdi/arrow-up-circle";
import VolumeIcon from "~icons/mdi/volume";
import MuteIcon from "~icons/mdi/volume-mute";
import CloseIcon from "~icons/mdi/close-circle";

const eq = <T,>(a: T, b: T): boolean => a === b;

export interface ChannelRowSignature {
  Args: {
    channel: Channel;
    tick?: number | null;
    onDeleteChannel?: () => void;
    onPreviewSound?: (sound: Sound) => void;
    onNextSound?: () => void;
    onPreviousSound?: () => void;
    onToggleMute?: () => void;
    onAddStep?: () => void;
    onRemoveStep?: () => void;
    onStepClick?: (step: Step, channel: Channel, index: number) => void;
  };
  Element: HTMLDivElement;
}

export default class ChannelRowComponent extends Component<ChannelRowSignature> {
  get channel(): Channel {
    return this.args.channel;
  }

  get steps(): Step[] {
    return this.channel.steps ?? [];
  }

  get stepCount(): number {
    return this.steps.length;
  }

  get activeStepIndex(): number | undefined {
    if (this.args.tick === undefined || this.args.tick === null || this.stepCount === 0) {
      return undefined;
    }
    return this.args.tick % this.stepCount;
  }

  get isMuted(): boolean {
    return this.channel.isMuted ?? false;
  }

  get sound(): Sound {
    return this.channel.sound;
  }

  get canRemoveStep(): boolean {
    return this.stepCount > 1;
  }

  get cannotRemoveStep(): boolean {
    return !this.canRemoveStep;
  }

  get canAddStep(): boolean {
    return this.stepCount < 64;
  }

  get cannotAddStep(): boolean {
    return !this.canAddStep;
  }

  handlePreviousSound = () => {
    this.args.onPreviousSound?.();
  };

  handleNextSound = () => {
    this.args.onNextSound?.();
  };

  handlePreviewSound = () => {
    this.args.onPreviewSound?.(this.channel.sound);
  };

  handleToggleMute = () => {
    this.args.onToggleMute?.();
  };

  handleDeleteChannel = () => {
    this.args.onDeleteChannel?.();
  };

  handleStepClick = (step: Step, index: number) => {
    this.args.onStepClick?.(step, this.channel, index);
  };

  handleAddStep = () => {
    if (this.canAddStep) {
      this.args.onAddStep?.();
    }
  };

  handleRemoveStep = () => {
    if (this.canRemoveStep) {
      this.args.onRemoveStep?.();
    }
  };

  <template>
    {{! Layout of this component: }}
    {{!       [⬆️⬇️][Sound Name][🔈✖️][▢▢▢▢ ▢▢▢▢ ▢▢▢▢ ▢▢▢▢][-+]            }}
    {{!        ↑     ↑           ↑     ↑                    ↑              }}
    {{!        |     |           |     |                    |              }}
    {{!        |     |           |     |                    steps-controls }}
    {{!        |     |           |     |                                   }}
    {{!        |     |           |     steps                               }}
    {{!        |     |           |                                         }}
    {{!        |     |           controls                                  }}
    {{!        |     |                                                     }}
    {{!        |     sounds                                                }}
    {{!        |                                                           }}
    {{!        channel-cycle                                               }}
    <div
      class="flex justify-between items-start gap-2 transition-all duration-150
        {{if this.isMuted 'opacity-70 '}}"
      data-test-channel-row
      data-test-channel-sound={{this.sound.id}}
      data-test-step-count={{this.stepCount}}
      data-test-channel-muted={{if this.isMuted "true" "false"}}
      ...attributes
    >

      {{! Part: channel-cycle }}
      {{! ======================================================}}

      <div class="w-4" data-test-channel-part="channel-cycle">
        <div class="flex flex-col items-center">
          <button
            type="button"
            class="hover:text-yellow-200"
            title="Previous Sound"
            data-test-prev-sound
            {{on "click" this.handlePreviousSound}}
          >
            <UpIcon />
          </button>
          <button
            type="button"
            class="hover:text-yellow-200"
            title="Remove Step"
            data-test-next-sound
            {{on "click" this.handleNextSound}}
          >
            <DownIcon />
          </button>
        </div>
      </div>

      {{! Part: sounds }}
      {{! ======================================================}}
      <div class="w-56" data-test-channel-part="sounds">
        <button
          type="button"
          class="h-10 w-full rounded
            {{this.sound.colorClass}}
            text-lg font-bold tracking-wider text-white"
          title="Preview Sound"
          data-test-preview-sound
          {{on "click" this.handlePreviewSound}}
        >
          {{this.sound.name}}
        </button>
      </div>

      {{! Part: controls }}
      {{! ======================================================}}
      <div class="w-4" data-test-channel-part="controls">
        <div class="flex flex-col items-center">
          <button
            type="button"
            class="{{if this.isMuted 'text-red-500' 'text-white'}} hover:text-yellow-200"
            title={{if this.isMuted "Unmute Channel" "Mute Channel"}}
            data-test-mute-toggle
            {{on "click" this.handleToggleMute}}
          >
            {{#if this.isMuted}}
              <MuteIcon />
            {{else}}
              <VolumeIcon />
            {{/if}}
          </button>
          <button
            type="button"
            class="hover:text-yellow-200"
            title="Delete Channel"
            data-test-delete-channel
            {{on "click" this.handleDeleteChannel}}
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {{! Part: steps }}
      {{! ======================================================}}
      <div class="flex-1" data-test-channel-part="steps">
        <div class="grid grid-cols-16 gap-1 steps-grid" data-test-steps-container>
          {{#each this.steps as |step idx|}}
            <StepPad
              @step={{step}}
              @isCurrentStep={{eq idx this.activeStepIndex}}
              @onClick={{fn this.handleStepClick step idx}}
              data-test-step-pad={{idx}}
            />
          {{/each}}
        </div>
      </div>

      {{! Part: steps-controls }}
      {{! ======================================================}}
      <div class="w-4" data-test-channel-part="steps-controls">
        <div class="flex flex-col items-center">
          <button
            type="button"
            class="hover:text-yellow-200"
            title="Add Step"
            disabled={{this.cannotAddStep}}
            data-test-add-step
            {{on "click" this.handleAddStep}}
          >
            <PlusIcon />
          </button>
          <button
            type="button"
            class="hover:text-yellow-200"
            title="Remove Step"
            disabled={{this.cannotRemoveStep}}
            data-test-remove-step
            {{on "click" this.handleRemoveStep}}
          >
            <MinusIcon />
          </button>
        </div>
      </div>

    </div>
  </template>
}
